import {
  EmployeeType,
  InterchangeTransferDetailsFormDataType,
} from "@type/HRM/interchangeTransfer";
import { UploadFileType } from "@type/HRM/upload-file";
import { INTERCHANGE_TRANSFER } from "@validations/HRM/interchangeTransfer/interchangeTransferValidation";

const {
  SOURCE_EMPLOYEE_PAY_SCALE_MAX,
  SOURCE_EMPLOYEE_PAY_SCALE_MIN,
  SOURCE_EMPLOYEE_POSITION,

  TARGET_EMPLOYEE_PAY_SCALE_MAX,
  TARGET_EMPLOYEE_PAY_SCALE_MIN,
  TARGET_EMPLOYEE_POSITION,
} = INTERCHANGE_TRANSFER;

export const revertMultiDocumentSingleFileData = (
  sourceEmployee: EmployeeType | undefined,
  targetEmployee: EmployeeType | undefined,
  docArrName: string[],
) => {
  let convertedData = {};
  let sourceEmployeeRawData: Record<string, any> = { ...sourceEmployee };

  if (!sourceEmployeeRawData) {
    return sourceEmployeeRawData;
  }

  // add prefix to sourceEmployeeRawData
  sourceEmployeeRawData = Object.fromEntries(
    Object.entries(sourceEmployeeRawData).map(([key, value]) => [
      `sourceEmployee-${key}`,
      value,
    ]),
  );

  // add data from employee to sourceEmployeeRawData
  sourceEmployeeRawData[`${SOURCE_EMPLOYEE_POSITION}`] =
    sourceEmployee?.employee?.unitPositionId;
  sourceEmployeeRawData[`${SOURCE_EMPLOYEE_PAY_SCALE_MIN}`] =
    sourceEmployee?.employee?.scaleFrom;
  sourceEmployeeRawData[`${SOURCE_EMPLOYEE_PAY_SCALE_MAX}`] =
    sourceEmployee?.employee?.scaleTo;

  docArrName.forEach((doc) => {
    const documentDetails = sourceEmployeeRawData[
      doc as keyof typeof sourceEmployeeRawData
    ] as UploadFileType;

    if (documentDetails) {
      sourceEmployeeRawData[`documentName-${documentDetails.documentId}`] =
        documentDetails.documentName;

      sourceEmployeeRawData[`remarks-${documentDetails.documentId}`] =
        documentDetails.remarks;

      sourceEmployeeRawData[`memorialNo-${documentDetails.documentId}`] =
        documentDetails.memorialNo;

      sourceEmployeeRawData[`date-${documentDetails.documentId}`] =
        documentDetails.date;

      sourceEmployeeRawData[doc] = { ...documentDetails };
      convertedData = { ...sourceEmployeeRawData };
    } else {
      convertedData = { ...sourceEmployeeRawData };
    }
  });

  // for targetEmployee
  let targetEmployeeRawData: Record<string, any> = { ...targetEmployee };
  if (!targetEmployeeRawData) {
    return targetEmployeeRawData;
  }
  // add prefix to targetEmployeeRawData
  targetEmployeeRawData = Object.fromEntries(
    Object.entries(targetEmployeeRawData).map(([key, value]) => [
      `targetEmployee-${key}`,
      value,
    ]),
  );

  // add data from employee to targetEmployeeRawData
  targetEmployeeRawData[`${TARGET_EMPLOYEE_POSITION}`] =
    targetEmployee?.employee?.unitPositionId;
  targetEmployeeRawData[`${TARGET_EMPLOYEE_PAY_SCALE_MIN}`] =
    targetEmployee?.employee?.scaleFrom;
  targetEmployeeRawData[`${TARGET_EMPLOYEE_PAY_SCALE_MAX}`] =
    targetEmployee?.employee?.scaleTo;

  docArrName.forEach((doc) => {
    const documentDetails = targetEmployeeRawData[
      doc as keyof typeof targetEmployeeRawData
    ] as UploadFileType;
    if (documentDetails) {
      targetEmployeeRawData[`documentName-${documentDetails.documentId}`] =
        documentDetails.documentName;
      targetEmployeeRawData[`remarks-${documentDetails.documentId}`] =
        documentDetails.remarks;
      targetEmployeeRawData[`memorialNo-${documentDetails.documentId}`] =
        documentDetails.memorialNo;
      targetEmployeeRawData[`date-${documentDetails.documentId}`] =
        documentDetails.date;
      targetEmployeeRawData[doc] = { ...documentDetails };
      convertedData = { ...convertedData, ...targetEmployeeRawData };
    } else {
      convertedData = { ...targetEmployeeRawData };
    }
  });

  return {
    ...sourceEmployeeRawData,
    ...targetEmployeeRawData,
  };
};

export const convertFormDataForSubmission = (
  formData: Record<string, any>,
  docTypes: string[] = [
    "transferOrderDocs",
    "assetHandOverDocs",
    "supportingDocs",
    "nocDocs",
    "joiningLetterDocs",
  ],
): InterchangeTransferDetailsFormDataType | {} => {
  if (!formData) return {};
  // Initialize result structure
  const result: {
    sourceEmployee: Record<string, any>;
    targetEmployee: Record<string, any>;
  } = {
    sourceEmployee: {
      employeeId: 0,
      releaseDate: "",
      joiningDate: "",
      toDate: "",
      basicSalary: 0,
    },
    targetEmployee: {
      employeeId: 0,
      releaseDate: "",
      joiningDate: "",
      toDate: "",
      basicSalary: 0,
    },
  };

  // Process source employee data
  Object.entries(formData).forEach(([key, value]) => {
    // Handle source employee fields
    if (key.startsWith("sourceEmployee-")) {
      const actualKey = key.replace("sourceEmployee-", "");

      // Skip the document fields as we'll handle them separately
      if (!docTypes.includes(actualKey)) {
        result.sourceEmployee[actualKey] = value;
      }
    }
    // Handle target employee fields
    else if (key.startsWith("targetEmployee-")) {
      const actualKey = key.replace("targetEmployee-", "");

      // Skip the document fields as we'll handle them separately
      if (!docTypes.includes(actualKey)) {
        result.targetEmployee[actualKey] = value;
      }
    }
  });

  // Extract document data for source employee
  docTypes.forEach((docType) => {
    // Find the document in the form data
    const sourceDocKey = Object.keys(formData).find(
      (key) => key === docType || key === `sourceEmployee-${docType}`,
    );

    if (sourceDocKey && formData[sourceDocKey]) {
      const doc = formData[sourceDocKey];
      const docId = doc.documentId;

      // Find any metadata fields for this document
      const documentName =
        formData[`documentName-${docId}`] || doc.documentName || "";
      const date = formData[`date-${docId}`] || doc.date || "";
      const memorialNo =
        formData[`memorialNo-${docId}`] || doc.memorialNo || "";
      const remarks = formData[`remarks-${docId}`] || doc.remarks || "";

      // Create document object with all required fields
      result.sourceEmployee[docType] = {
        name: doc.name || "",
        mimeType: doc.mimeType || "",
        fileId: doc.fileId || "",
        documentId: docId || "",
        documentType:
          doc.documentType || "supportingDocument.uploadSupportingDocument",
        memorialNo,
        remarks,
        documentName,
        date,
      };
    }
  });

  // Extract document data for target employee
  docTypes.forEach((docType) => {
    // Find the document in the form data
    const targetDocKey = Object.keys(formData).find(
      (key) => key === `targetEmployee-${docType}`,
    );

    if (targetDocKey && formData[targetDocKey]) {
      const doc = formData[targetDocKey];
      const docId = doc.documentId;

      // Find any metadata fields for this document
      const documentName =
        formData[`documentName-${docId}`] || doc.documentName || "";
      const date = formData[`date-${docId}`] || doc.date || "";
      const memorialNo =
        formData[`memorialNo-${docId}`] || doc.memorialNo || "";
      const remarks = formData[`remarks-${docId}`] || doc.remarks || "";

      // Create document object with all required fields
      result.targetEmployee[docType] = {
        name: doc.name || "",
        mimeType: doc.mimeType || "",
        fileId: doc.fileId || "",
        documentId: docId || "",
        documentType:
          doc.documentType || "supportingDocument.uploadSupportingDocument",
        memorialNo,
        remarks,
        documentName,
        date,
      };
    }
  });

  return result;
};
